// src/App.js
import React, { useEffect } from "react";
import { HashRouter as Router, Routes, Route } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import "./api"; // initialize axios interceptors

import "bootstrap/dist/css/bootstrap.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";

// Pages
import Dashboard from "./pages/Dashboard/DashboardPage";
import Promotions from "./pages/Promotions/PromotionsPage";
import Complaints from "./pages/Complaints/ComplaintsPage";
import Requests from "./pages/Requests/RequestsPage";
import Notifications from "./pages/Notifications/NotificationsPage";
import Customers from "./pages/Customers/CustomersPage";
import Login from "./pages/auth/LoginPage";
import Register from "./pages/auth/registerPage";
import AppVersionPage from "./pages/Appversion/AppVersionPage";
import OtpLookupPage from "./pages/OtpLookup/OtpLookupPage";
import Payment from "./pages/Payment/PaymentPage";
import ForbiddenPage from "./pages/Forbidden/ForbiddenPage";
import ProfilePage from "./pages/Profile/ProfilePage";

// Guard
import PrivateRoute from "./components/PrivateRoute";

const App = () => {
  // Auto logout when token expires (timer + periodic check)
  useEffect(() => {
    let logoutTimerId = null;

    const scheduleLogout = () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        if (logoutTimerId) {
          clearTimeout(logoutTimerId);
          logoutTimerId = null;
        }
        return;
      }
      try {
        const decoded = jwtDecode(token);
        const expiresAt = decoded?.exp ? decoded.exp * 1000 : 0;
        const remaining = expiresAt - Date.now();
        if (remaining <= 0) {
          localStorage.removeItem("authToken");
          window.location.hash = "/";
          return;
        }
        if (logoutTimerId) clearTimeout(logoutTimerId);
        logoutTimerId = setTimeout(() => {
          localStorage.removeItem("authToken");
          window.location.hash = "/";
        }, remaining);
      } catch (err) {
        // Invalid token → logout
        localStorage.removeItem("authToken");
        window.location.hash = "/";
      }
    };

    // Initial schedule
    scheduleLogout();
    // Poll occasionally to catch token changes set within same tab
    const pollId = setInterval(scheduleLogout, 30000); // every 30s

    return () => {
      if (logoutTimerId) clearTimeout(logoutTimerId);
      clearInterval(pollId);
    };
  }, []);

  return (
    <Router>
      <Routes>
        {/* Public */}
        <Route path="/" element={<Login />} />
        <Route path="/403" element={<ForbiddenPage />} />

        {/* Protected (auth only) */}
        <Route
          path="/dashboard"
          element={<PrivateRoute element={<Dashboard />} />}
        />
        <Route
          path="/profile"
          element={<PrivateRoute element={<ProfilePage />} />}
        />

        {/* Protected + Role-based */}
        <Route
          path="/promotions"
          element={
            <PrivateRoute
              element={<Promotions />}
              allow={["admin", "branding"]}
            />
          }
        />
        <Route
          path="/assistances"
          element={
            <PrivateRoute element={<Complaints />} allow={["admin", "Csd"]} />
          }
        />
        <Route
          path="/requests"
          element={
            <PrivateRoute element={<Requests />} allow={["admin", "Csd"]} />
          }
        />
        <Route
          path="/notifications"
          element={
            <PrivateRoute
              element={<Notifications />}
              allow={["admin", "manager"]}
            />
          }
        />
        <Route
          path="/customers"
          element={
            <PrivateRoute
              element={<Customers />}
              allow={["admin", "manager"]}
            />
          }
        />
        <Route
          path="/register"
          element={<PrivateRoute element={<Register />} allow={["admin"]} />}
        />
        <Route
          path="/appversion"
          element={
            <PrivateRoute element={<AppVersionPage />} allow={["admin"]} />
          }
        />
        <Route
          path="/payment"
          element={<PrivateRoute element={<Payment />} allow={["admin"]} />}
        />
        <Route
          path="/otp-lookup"
          element={
            <PrivateRoute
              element={<OtpLookupPage />}
              allow={["admin", "Csd"]}
            />
          }
        />
      </Routes>
    </Router>
  );
};

export default App;
